// src/components/OrgImageryEntitlements.jsx
//
// Read-only org-admin view of imagery entitlements.
// Mirrors the platform-admin OrgEntitlementsPanel but without grant/edit
// affordances — customers see what they have, not how to change it.
import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { fetchWithAuth } from "../api/fetchWithAuth";

export default function OrgImageryEntitlements() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    async function load() {
      try {
        const res = await fetchWithAuth("/api/v1/org/imagery-entitlements");
        if (!res.ok) throw new Error("Failed to load imagery entitlements");
        setData(await res.json());
      } catch (e) {
        console.error(e);
        setError(e.message);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center pt-6">
        <Loader2 className="animate-spin text-gray-400" size={20} />
      </div>
    );
  }

  if (error) return <div className="text-xs text-red-400">{error}</div>;
  if (!data) return null;

  const { sku_summary = [], entitlements = [] } = data;

  if (sku_summary.length === 0 && entitlements.length === 0) {
    return (
      <div className="text-gray-400 italic mt-4">
        No imagery entitlements for this organization.
      </div>
    );
  }

  return (
    <div className="space-y-4 mt-4">
      {/* SKU POOL SUMMARY */}
      <div className="rounded border border-bg-border bg-bg p-3 space-y-3">
        <div className="text-xs text-gray-300 font-semibold">SKU Pools</div>

        {sku_summary.map(s => {
          const consumed = Number(s.sq_mi_consumed) || 0;
          const total = Number(s.sq_mi_purchased_total) || 0;
          const pct = total > 0 ? (consumed / total) * 100 : 0;
          const available = Math.max(0, total - consumed);

          return (
            <div key={s.sku_code}>
              <div className="flex justify-between items-center text-xs">
                <span className="text-gray-400">
                  {s.display_name}
                  <span className="text-fg-secondary ml-2">
                    ({s.entitlement_count} grant{s.entitlement_count !== 1 ? "s" : ""})
                  </span>
                </span>
                <span className="text-gray-100">
                  {consumed.toFixed(1)} / {total.toFixed(1)} sq mi{" "}
                  <span className="text-fg-secondary font-normal">
                    ({available.toFixed(1)} available)
                  </span>
                </span>
              </div>
              <div className="mt-1 h-1.5 rounded bg-bg-soft">
                <div
                  className={`h-1.5 rounded transition-[width] duration-200 ${
                    pct >= 100 ? "bg-red-500"
                      : pct >= 80 ? "bg-yellow-500"
                        : "bg-status-ok"
                  }`}
                  style={{ width: `${Math.min(pct, 100)}%` }}
                />
              </div>
            </div>
          );
        })}
      </div>

      {/* ENTITLEMENTS TABLE */}
      <div>
        <div className="text-xs text-gray-300 font-semibold mb-2">
          Entitlements
        </div>

        <table className="w-full text-xs">
          <thead className="text-gray-400">
            <tr>
              <th className="text-left py-1">SKU</th>
              <th className="text-left py-1">Dataset</th>
              <th className="text-left py-1">Scope</th>
              <th className="text-left py-1">Allocated</th>
              <th className="text-left py-1">Consumed</th>
              <th className="text-left py-1">Term</th>
              <th className="text-left py-1">Contract</th>
              <th className="text-left py-1">Status</th>
            </tr>
          </thead>
          <tbody>
            {entitlements.map(e => {
              const termStart = new Date(e.term_start).toLocaleDateString();
              const termEnd = e.term_end
                ? new Date(e.term_end).toLocaleDateString()
                : "—";
              const datasetLabel = `${e.dataset_basin}/${e.dataset_region} ${e.dataset_acquisition_date}`;
              const scopeLabel = e.scope === "aoi"
                ? `AOI: ${e.coverage_aoi_name || "—"}`
                : "full";

              const statusColor = {
                active: "border-green-500/40 text-green-400",
                suspended: "border-yellow-500/40 text-yellow-400",
                revoked: "border-red-500/40 text-red-400",
                expired: "border-gray-500/40 text-gray-400",
              }[e.status] || "border-gray-500/40 text-gray-400";

              return (
                <tr key={e.id} className="border-t border-bg-border">
                  <td className="py-1.5">{e.sku_display_name}</td>
                  <td className="py-1.5 text-gray-300">{datasetLabel}</td>
                  <td className="py-1.5 text-gray-400">{scopeLabel}</td>
                  <td className="py-1.5">
                    {e.scope === "aoi" && e.coverage_aoi_area_sqmi != null
                      ? `${Number(e.coverage_aoi_area_sqmi).toFixed(1)} / ${Number(e.sq_mi_purchased).toFixed(1)}`
                      : `— / ${Number(e.sq_mi_purchased).toFixed(1)}`}
                  </td>
                  <td className="py-1.5 text-gray-300">
                    {Number(e.sq_mi_consumed).toFixed(1)}
                  </td>
                  <td className="py-1.5 text-gray-400">
                    {termStart} – {termEnd}
                  </td>
                  <td className="py-1.5 text-gray-400">
                    {e.contract_ref || "—"}
                  </td>
                  <td className="py-1.5">
                    <span className={`px-2 py-0.5 rounded border ${statusColor}`}>
                      {e.status}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}